import { Callback } from '../../../types';
import { isMobile } from '../../../utils';
import * as Video from 'twilio-video';
import { ConnectOptions, LocalTrack, Room } from 'twilio-video';
import { VideoRoomMonitor } from '@twilio/video-room-monitor';
import { useCallback, useEffect, useRef, useState } from 'react';
import { observeCall } from '../../../utils/callReliability/diagnostics';

export default function useRoom(
	localTracks: LocalTrack[],
	onError: Callback,
	options?: ConnectOptions,
) {
	const [room, setRoom] = useState<Room | null>(null);
	const [isConnecting, setIsConnecting] = useState(false);
	const [disconnectedUnexpectedly, setUnexpected] = useState(false);
	const latest = useRef({ localTracks, options });
	latest.current = { localTracks, options };
	const generation = useRef(0);
	const pending = useRef<Promise<void>>();
	const cancelPending = useRef<() => void>();
	const call = useRef<Room>();
	const alive = useRef(true);
	const stopObserving = useRef<() => void>();
	const cancelConnect = useCallback(() => {
		generation.current++;
		cancelPending.current?.();
	}, []);
	useEffect(() => {
		alive.current = true;
		return () => {
			alive.current = false;
			cancelConnect();
			call.current?.disconnect();
			stopObserving.current?.();
		};
	}, [cancelConnect]);
	const connect = useCallback((token: string) => {
		if (pending.current) return pending.current;
		if (call.current?.state === 'connected') return Promise.resolve();
		if (!token?.trim())
			return Promise.reject(
				new Error('Unable to join the call. Please try again.'),
			);
		const attempt = ++generation.current;
		setIsConnecting(true);
		const operation = new Promise<void>((resolve, reject) => {
			let settled = false;
			const fail = (error: Error) => {
				if (!settled) {
					settled = true;
					reject(error);
				}
			};
			const timer = setTimeout(() => {
				generation.current++;
				fail(new Error('Joining took too long. Please try again.'));
			}, 30000);
			cancelPending.current = () => {
				clearTimeout(timer);
				fail(new Error('Joining cancelled.'));
			};
			void Video.connect(token, {
				...latest.current.options,
				tracks: latest.current.localTracks,
			}).then(
				(newRoom) => {
					clearTimeout(timer);
					if (
						!alive.current ||
						attempt !== generation.current ||
						settled
					) {
						newRoom.disconnect();
						return;
					}
					settled = true;
					call.current = newRoom;
					setRoom(newRoom);
					setUnexpected(false);
					VideoRoomMonitor.registerVideoRoom(newRoom);
					stopObserving.current = observeCall(newRoom);
					newRoom.setMaxListeners(20);
					const disconnect = () => newRoom.disconnect();
					newRoom.once('disconnected', (_, error) => {
						call.current = undefined;
						if (alive.current) {
							setUnexpected(Boolean(error));
							setTimeout(() => {
								if (alive.current)
									setRoom((current) =>
										current === newRoom ? null : current,
									);
							});
						}
						window.removeEventListener('beforeunload', disconnect);
						if (isMobile)
							window.removeEventListener('pagehide', disconnect);
					});
					newRoom.localParticipant.videoTracks.forEach(
						(publication) => publication.setPriority('low'),
					);
					window.addEventListener('beforeunload', disconnect);
					if (isMobile)
						window.addEventListener('pagehide', disconnect);
					resolve();
				},
				(error) => {
					clearTimeout(timer);
					fail(error);
				},
			);
		});
		pending.current = operation.finally(() => {
			pending.current = undefined;
			cancelPending.current = undefined;
			if (alive.current) setIsConnecting(false);
		});
		return pending.current;
	}, []);
	return {
		room,
		isConnecting,
		connect,
		cancelConnect,
		disconnectedUnexpectedly,
	};
}
